import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { useToast } from './Toast';
import { useUpdateMe } from '../../features/users/hooks';
import { useAppStore } from '../../store/useAppStore';

const schema = z.object({
  phone: z.string().min(7, 'Enter a valid phone number'),
  company: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const inputClass =
  'w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors';
const labelClass = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';

type Props = {
  open: boolean;
};

export function CompleteProfileModal({ open }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const user = useAppStore((s) => s.user);
  const updateMe = useUpdateMe();
  const { toast } = useToast();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: { phone: '', company: user?.company ?? '' },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await updateMe.mutateAsync(data);
      toast('Profile completed.', 'success');
    } catch {
      toast("Couldn't save that, please try again.", 'error');
    }
  };

  return (
    <Modal
      open={open && !dismissed}
      onClose={() => setDismissed(true)}
      title="Complete your profile"
      maxWidth="max-w-md"
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-center gap-3 text-center pb-1">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
            <UserCircle size={26} className="text-accent" />
          </div>
          <p className="text-sm text-muted leading-relaxed max-w-sm">
            Just a couple details so we can reach you about your bookings.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div>
            <label className={labelClass}>Phone</label>
            <input type="tel" className={inputClass} placeholder="+1 876 XXX XXXX" {...register('phone')} />
            {errors.phone && <p className="text-xs text-danger mt-1">{errors.phone.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Company <span className="text-muted normal-case font-normal tracking-normal">(optional)</span></label>
            <input className={inputClass} placeholder="Your company" {...register('company')} />
          </div>

          <div className="flex justify-between items-center gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDismissed(true)}
              className="!text-muted text-sm !tracking-normal rounded-xl"
            >
              Maybe later
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={updateMe.isPending}
              loadingText="Saving…"
              className="!text-sm normal-case !tracking-normal rounded-xl !px-4 !py-2.5"
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
